import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { STORAGE_KEYS, getAvailableViews } from "../services/navigation";
import { getMyProfile } from "../services/employeeApi";
import { clearAllDrafts } from "../hooks/useFormDraft";
import { logoutUser, pingSession } from "../services/authApi";
import { getErrorMessage } from "../utils/errors";
import { clearStoredSession, sessionIsOver } from "../services/api";

const api = { getMyProfile, logoutUser, pingSession };

const KEEP_ALIVE_MS = 4 * 60 * 1000;
const MIN_PING_GAP_MS = 60 * 1000;

const RoleContext = createContext(null);

function readSession() {
  const username = localStorage.getItem(STORAGE_KEYS.username);
  const roleKey = localStorage.getItem(STORAGE_KEYS.role);
  const token = localStorage.getItem(STORAGE_KEYS.accessToken);
  return username && roleKey && token ? { username, roleKey } : null;
}

export function RoleProvider({ children }) {
  const [session, setSessionState] = useState(readSession);
  const [profile, setProfile] = useState(null);
  const [profileError, setProfileError] = useState(null);
  const [activeView, setActiveViewState] = useState(() => localStorage.getItem(STORAGE_KEYS.activeView));
  const [counters, setCounters] = useState({ unread: 0, pendingApprovals: 0, pendingAssessments: 0, pendingProgress: 0, myGoalsUpdate: 0, unreadByLink: {} });

  const setSession = useCallback((username, roleKey) => {
    localStorage.removeItem(STORAGE_KEYS.activeView);
    setActiveViewState(null);
    setProfile(null);
    setProfileError(null);
    setSessionState({ username, roleKey });
  }, []);

  const loadProfile = useCallback(async () => {
    if (!session) return;
    try {
      const { data } = await api.getMyProfile();
      setProfile(data);
      setProfileError(null);
    } catch (error) {
      setProfileError(getErrorMessage(error));
    }
  }, [session]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // The keep-alive ping found that the stored login is dead (expired, signed out elsewhere, server restarted).
  // Remove the stale token so the tab stops pinging and the user is sent to the sign-in page.
  // Unsaved form drafts are kept, so they come back after signing in again.
  const expireStaleSession = useCallback(() => {
    clearStoredSession();
    Object.values(STORAGE_KEYS).forEach((key) => {
      if (key !== STORAGE_KEYS.selectedCycle) localStorage.removeItem(key);
    });
    sessionStorage.setItem("pms_login_notice", "Your session has expired. Please sign in again.");
    setSessionState(null);
    setProfile(null);
    setActiveViewState(null);
  }, []);

  const lastPingRef = useRef(0);
  useEffect(() => {
    if (!session) return undefined;
    const ping = () => {
      // [NEW] no keep-alive call when the tab is in the background, and none with a login that is already over:
      // the session is ended here, without sending the old token to the backend (no SESSION_INVALID rejections).
      if (sessionIsOver()) { expireStaleSession(); return; }
      if (document.visibilityState === "hidden") return;
      const now = Date.now();
      if (now - lastPingRef.current < MIN_PING_GAP_MS) return;
      lastPingRef.current = now;
      const sentToken = localStorage.getItem(STORAGE_KEYS.accessToken);
      api.pingSession().catch((error) => {
        // Only react if this tab is still on the same login: another tab may have signed in again meanwhile.
        const stillSame = localStorage.getItem(STORAGE_KEYS.accessToken) === sentToken;
        if (error?.response?.status === 401 && stillSame) expireStaleSession();
      });
    };
    const onVisible = () => { if (document.visibilityState === "visible") ping(); };
    const timer = window.setInterval(ping, KEEP_ALIVE_MS);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [session, expireStaleSession]);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.storageArea !== localStorage) return;
      if (event.key !== null && ![STORAGE_KEYS.accessToken, STORAGE_KEYS.username, STORAGE_KEYS.role].includes(event.key)) return;
      const next = readSession();
      setSessionState((current) => {
        if (!next) return null;
        if (current && current.username === next.username && current.roleKey === next.roleKey) return current;
        return next;
      });
      if (!next) {
        setProfile(null);
        setActiveViewState(null);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const logout = useCallback((options) => {
    const localOnly = Boolean(options && options.local === true);
    const token = localStorage.getItem(STORAGE_KEYS.accessToken);
    if (!localOnly && token) api.logoutUser(token).catch(() => {});
    // [NEW] Signing out deletes unsaved form drafts, so the next person on this computer cannot see them.
    // (An expired session does not call logout(), so drafts survive and come back after signing in again.)
    clearAllDrafts();
    // [NEW] PM-12: forget the Microsoft account cached by MSAL in this tab.
    import("../services/entraAuth").then((m) => m.clearMicrosoftAccount()).catch(() => {});
    Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
    setSessionState(null);
    setProfile(null);
    setActiveViewState(null);
  }, []);

  const roleKey = profile?.role || session?.roleKey || null;
  const roleKeys = useMemo(() => profile?.roleKeys || (roleKey ? [roleKey] : []), [profile, roleKey]);
  const availableViews = useMemo(() => getAvailableViews(roleKeys), [roleKeys]);
  const currentView = availableViews.includes(activeView) ? activeView : availableViews[0];

  const setActiveView = useCallback(
    (view) => {
      if (!availableViews.includes(view)) return;
      localStorage.setItem(STORAGE_KEYS.activeView, view);
      setActiveViewState(view);
    },
    [availableViews]
  );

  const value = useMemo(
    () => ({
      session,
      isAuthenticated: Boolean(session),
      setSession,
      logout,
      profile,
      profileError,
      profileLoading: Boolean(session) && !profile && !profileError,
      reloadProfile: loadProfile,
      roleKey,
      roleKeys,
      availableViews,
      activeView: currentView,
      setActiveView,
      hasView: (view) => availableViews.includes(view),
      counters,
      setCounters,
    }),
    [session, setSession, logout, profile, profileError, loadProfile, roleKey, roleKeys, availableViews, currentView, setActiveView, counters]
  );

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

export function useRole() {
  const ctx = useContext(RoleContext);
  if (!ctx) throw new Error("useRole must be used inside RoleProvider");
  return ctx;
}